if (!customElements.get('apella-wardrobe')) {
  customElements.define('apella-wardrobe', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.views = [...this.querySelectorAll('[data-view]')];
      this.dialog = this.querySelector('dialog');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.hovered = false;
      this.visible = false;
      this.setPaused(this.dataset.autoplay !== 'true' || this.dataset.editor === 'true' || this.motion.matches);
      this.addEventListener('click', (event) => {
        if (Date.now() < (this.ignoreClickUntil || 0)) {
          event.preventDefault();
          return;
        }
        const layout = event.target.closest('[data-layout-button]');
        const choice = event.target.closest('[data-choice], [data-dot], [data-slide-choice]');
        const demo = event.target.closest('[data-demo-link]');
        const step = event.target.closest('[data-slide-step]');
        if (layout) this.setLayout(layout.dataset.layoutButton, true);
        if (choice) {
          this.setPaused(true);
          if (choice.dataset.choice === this.dataset.active && this.dataset.editor !== 'true') {
            const look = document.getElementById(choice.getAttribute('aria-controls'));
            const destination = look?.querySelector('a.aw__image-link[href]');
            if (destination && this.contains(look)) {
              destination.click();
              return;
            }
          }
          this.setActive(choice.dataset.choice || choice.dataset.dot || choice.dataset.slideChoice);
        }
        if (step) {
          this.animateNavigationPush(step);
          this.setPaused(true);
          this.advance(Number(step.dataset.slideStep));
        }
        if (demo && this.dialog) {
          this.querySelector('[data-destination-title]').textContent = demo.dataset.demoLink;
          this.dialog.showModal();
        }
        if (event.target.closest('[data-close]')) this.dialog?.close();
      }, options);
      this.addEventListener('focusin', (event) => {
        this.setPaused(true);
        const choice = event.target.closest('[data-choice]');
        if (choice && !this.isSlideshow()) this.setActive(choice.dataset.choice);
      }, options);
      this.addEventListener('pointerover', (event) => {
        if (this.isSlideshow()) return;
        if (event.pointerType !== 'mouse' || !matchMedia('(min-width: 750px) and (hover: hover)').matches) return;
        if (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible') && document.activeElement.closest('[data-view]')) return;
        const choice = event.target.closest('[data-choice]');
        if (choice && !this.isSlideshow()) this.setActive(choice.dataset.choice);
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const choice = event.target.closest('[data-choice], [data-dot]');
        if (!choice || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const buttons = [...choice.parentElement.closest('[role=group]').querySelectorAll('[data-choice], [data-dot]')];
        let index = buttons.indexOf(choice);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = buttons.length - 1;
        else {
          const columns = getComputedStyle(choice.parentElement).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
          const step = ['ArrowDown', 'ArrowUp'].includes(event.key) ? columns : 1;
          index = (index + (['ArrowDown', 'ArrowRight'].includes(event.key) ? step : -step) + buttons.length) % buttons.length;
        }
        event.preventDefault();
        buttons[index].focus({ preventScroll: true });
        this.setActive(buttons[index].dataset.choice || buttons[index].dataset.dot);
      }, options);
      this.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') this.hovered = true; }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; }, options);
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' && (event.target.closest('.aw__view--doors .aw__looks') || (event.target.closest('.aw__stage') && !event.target.closest('button, a')))) this.swipe = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, options);
      this.addEventListener('pointerup', (event) => {
        if (!this.swipe || this.swipe.id !== event.pointerId) return;
        const dx = event.clientX - this.swipe.x;
        const dy = event.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        this.setPaused(true);
        this.advance(dx < 0 ? 1 : -1);
        this.ignoreClickUntil = Date.now() + 500;
      }, options);
      this.addEventListener('pointercancel', () => { this.swipe = null; }, options);
      this.addEventListener('shopify:block:select', (event) => {
        this.setPaused(true);
        this.setActive(event.detail.blockId);
      }, options);
      this.motion.addEventListener('change', () => this.setPaused(this.motion.matches || this.paused), options);
      document.addEventListener('visibilitychange', () => { this.lastTick = performance.now(); }, options);
      this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting && entry.intersectionRatio >= .25; }, { threshold: .25 });
      this.observer.observe(this);
      const requested = this.dataset.review === 'true' ? new URL(location.href).searchParams.get('apella_wardrobe') : null;
      this.setLayout(requested || this.dataset.layout);
      this.setActive(this.dataset.active);
      this.classList.add('is-ready');
      this.autoplayDuration = Math.max(4, Number(this.dataset.interval) || 6) * 1000;
      this.elapsed = 0;
      this.lastTick = performance.now();
      this.updateProgress();
      this.timer = setInterval(() => {
        const now = performance.now();
        const delta = now - this.lastTick;
        this.lastTick = now;
        if (!this.paused && !this.hovered && this.visible && !document.hidden && this.isSlideshow()) {
          this.elapsed += delta;
          if (this.elapsed >= this.autoplayDuration) this.advance(1);
        }
        this.updateProgress();
      }, 100);
    }
    setLayout(layout, updateUrl = false) {
      if (!this.views.some(view => view.dataset.view === layout)) return;
      this.dataset.layout = layout;
      this.views.forEach(view => { view.hidden = view.dataset.view !== layout; });
      this.querySelectorAll('[data-layout-button]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.layoutButton === layout)));
      if (updateUrl && this.dataset.review === 'true' && this.dataset.editor !== 'true') {
        const url = new URL(location.href);
        url.searchParams.set('apella_wardrobe', layout);
        history.replaceState(history.state, '', url);
      }
    }
    setActive(key, direction) {
      if (![...this.querySelectorAll('[data-look]')].some(look => look.dataset.look === key)) return;
      if (this.classList.contains('is-ready') && this.dataset.active === key) return;
      const doors = [...(this.views.find(view => view.dataset.view === this.dataset.layout)?.querySelectorAll('[data-look]') || [])];
      const previous = doors.find(look => look.dataset.look === this.dataset.active);
      const incoming = doors.find(look => look.dataset.look === key);
      const travel = direction || (doors.indexOf(incoming) >= doors.indexOf(previous) ? 'next' : 'previous');
      this.clearSlideTransition();
      this.dataset.active = key;
      this.elapsed = 0;
      this.lastTick = performance.now();
      this.querySelectorAll('[data-choice], [data-dot]').forEach(button => button.setAttribute('aria-pressed', String((button.dataset.choice || button.dataset.dot) === key)));
      this.querySelectorAll('[data-intention]').forEach(row => {
        const selected = row.dataset.intention === key;
        row.classList.toggle('is-selected', selected);
        const link = row.querySelector('.aw__intention-shop');
        if (link) { link.hidden = !selected; link.inert = !selected; }
      });
      this.querySelectorAll('[data-look-copy]').forEach(copy => {
        copy.hidden = copy.dataset.lookCopy !== key;
        copy.inert = copy.hidden;
      });
      this.querySelectorAll('[data-look]').forEach(look => {
        const active = look.dataset.look === key;
        look.classList.toggle('is-active', active);
        look.inert = !active;
        look.setAttribute('aria-hidden', String(!active));
      });
      if (this.isSlideshow() && previous && incoming && previous !== incoming && this.classList.contains('is-ready') && !this.motion.matches) {
        previous.dataset.direction = travel;
        incoming.dataset.direction = travel;
        previous.classList.add('is-exiting');
        incoming.classList.add('is-entering');
        this.slideTransition = setTimeout(() => this.clearSlideTransition(), this.dataset.layout === 'immersive' ? 520 : 480);
      }
      this.updateProgress();
      const slides = this.querySelector('.aw__view--doors .aw__looks');
      if (slides) {
        const looks = [...slides.querySelectorAll('[data-look]')];
        const index = looks.findIndex(look => look.dataset.look === key);
        const count = this.querySelector('[data-slide-count]');
        if (count) count.textContent = `${index + 1} / ${looks.length}`;
        this.querySelectorAll('[data-slide-step]').forEach(button => { button.disabled = looks.length < 2; });
      }
    }
    updateProgress() {
      const progress = this.paused ? 1 : Math.min(1, (this.elapsed || 0) / (this.autoplayDuration || 6000));
      this.querySelectorAll('.aw__timed-dot').forEach(button => {
        const active = button.dataset.dot === this.dataset.active;
        button.querySelector('.aw__progress-value').style.strokeDashoffset = String(active ? 100 * (1 - progress) : 100);
      });
    }
    animateNavigationPush(button) {
      this.clearNavigationPush();
      this.activePushButton = button;
      button.classList.remove('is-pushing');
      void button.getBoundingClientRect();
      button.classList.add('is-pushing');
      this.navigationPushTimer = window.setTimeout(() => this.clearNavigationPush(), 640);
    }

    clearNavigationPush() {
      if (this.navigationPushTimer) window.clearTimeout(this.navigationPushTimer);
      this.navigationPushTimer = null;
      this.activePushButton?.classList.remove('is-pushing');
      this.activePushButton = null;
    }

    isSlideshow() {
      return ['doors', 'immersive'].includes(this.dataset.layout);
    }
    advance(direction) {
      const looks = [...(this.views.find(view => view.dataset.view === this.dataset.layout)?.querySelectorAll('[data-look]') || [])];
      if (looks.length < 2) return;
      const index = looks.findIndex(look => look.dataset.look === this.dataset.active);
      this.setActive(looks[(index + direction + looks.length) % looks.length].dataset.look, direction > 0 ? 'next' : 'previous');
    }
    clearSlideTransition() {
      clearTimeout(this.slideTransition);
      this.querySelectorAll('.is-entering, .is-exiting').forEach(look => {
        look.classList.remove('is-entering', 'is-exiting');
        delete look.dataset.direction;
      });
    }
    setPaused(paused) {
      this.paused = paused || this.motion?.matches;
      this.dataset.paused = String(this.paused);
      this.updateProgress();

    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.dialog?.close();
      clearInterval(this.timer);
      this.clearSlideTransition();
      this.observer?.disconnect();
      this.clearNavigationPush();
    }
  });
}
